import { format } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { RunDetail, ProvenanceEntry } from '@/api/types'

interface ProvenanceTabProps {
  run: RunDetail
}

/**
 * Agent to color mapping for timeline.
 */
const agentColors: Record<string, string> = {
  'classifier': 'bg-blue-500',
  'extractor': 'bg-green-500',
  'verifier': 'bg-purple-500',
  'researcher': 'bg-orange-500',
  'formatter': 'bg-pink-500',
  'router': 'bg-cyan-500',
}

function getAgentColor(agent: string): string {
  const key = agent.toLowerCase()
  for (const [pattern, color] of Object.entries(agentColors)) {
    if (key.includes(pattern)) {
      return color
    }
  }
  return 'bg-gray-500'
}

interface TimelineItemProps {
  entry: ProvenanceEntry
  isLast: boolean
}

function TimelineItem({ entry, isLast }: TimelineItemProps) {
  return (
    <div className="flex gap-4">
      {/* Timeline line and dot */}
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full ${getAgentColor(entry.agent)}`} />
        {!isLast && <div className="w-0.5 h-full bg-border flex-1 min-h-[2rem]" />}
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="outline">{entry.agent}</Badge>
          <span className="text-xs text-muted-foreground">
            {format(new Date(entry.timestamp), 'HH:mm:ss')}
          </span>
          {entry.confidence !== undefined && (
            <Badge variant="secondary" className="text-xs">
              {(entry.confidence * 100).toFixed(0)}% confidence
            </Badge>
          )}
        </div>
        <p className="font-medium">{entry.action}</p>
        {entry.decision && (
          <p className="text-sm text-muted-foreground mt-1">
            Decision: {entry.decision}
          </p>
        )}
      </div>
    </div>
  )
}

export function ProvenanceTab({ run }: ProvenanceTabProps) {
  // Get provenance data from run
  const provenance = run.provenance || []

  if (provenance.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">
            {run.status === 'processing'
              ? 'Agent decisions will appear as processing continues.'
              : 'No provenance data available for this run.'}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent Decision Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-0">
          {provenance.map((entry, idx) => (
            <TimelineItem
              key={`${entry.timestamp}-${idx}`}
              entry={entry}
              isLast={idx === provenance.length - 1}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
